import { useIntl } from 'react-intl';
import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Image,
  ActionIcon,
  Loader,
  Center,
  FileButton,
  Grid,
  Card,
  Badge,
  CopyButton,
  Tooltip,
  Modal,
} from '@mantine/core';
import {
  IconUpload,
  IconTrash,
  IconPhoto,
  IconCopy,
  IconCheck,
  IconLink,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router';
import { API_URL } from '@/commons/constants';
import { useAppDispatch, useAppSelector } from '@store';
import {
  uploadFile,
  deleteUpload,
  fetchUploads,
  type UploadedFile,
} from '@store/slices/uploadsSlice';

export const UserUploads = () => {
  const intl = useIntl();
  const { username } = useParams();
  const dispatch = useAppDispatch();
  const uploads = useAppSelector((state) => state.uploads.uploads);
  const isLoading = useAppSelector((state) => state.uploads.isLoading);
  const isUploading = useAppSelector((state) => state.uploads.isUploading);
  const error = useAppSelector((state) => state.uploads.error);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Show error notifications when error state changes
  useEffect(() => {
    if (error) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: error,
        color: 'red',
      });
    }
  }, [error, intl]);

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
    ];
    if (!allowedTypes.includes(file.type)) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'invalidFileType',
        }),
        color: 'red',
      });
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({
          id: 'fileTooLarge',
        }),
        color: 'red',
      });
      return;
    }

    try {
      await dispatch(uploadFile(file)).unwrap();

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({
          id: 'fileUploaded',
        }),
        color: 'green',
      });

      // Refresh the file list
      await dispatch(fetchUploads());

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      // Error is already handled in Redux and shown via useEffect
    }
  };

  const handleDeleteClick = (filename: string) => {
    setFileToDelete(filename);
    setDeleteDialogOpened(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    setIsDeleting(fileToDelete);
    setDeleteDialogOpened(false);
    try {
      await dispatch(deleteUpload(fileToDelete)).unwrap();

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({
          id: 'fileDeleted',
        }),
        color: 'green',
      });
    } catch (error: any) {
      // Error is already handled in Redux and shown via useEffect
    } finally {
      setIsDeleting(null);
      setFileToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpened(false);
    setFileToDelete(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getImageUrl = (file: UploadedFile): string => {
    // Use the GET uploaded file endpoint to serve the image
    const encodedUsername = encodeURIComponent(username || '');
    const encodedFilename = encodeURIComponent(file.filename);
    return `${API_URL}/users/${encodedUsername}/uploads/${encodedFilename}`;
  };

  const getImageHtmlTag = (file: UploadedFile): string => {
    const imageUrl = getImageUrl(file);
    return `<img src="${imageUrl}" alt="${file.originalName}" />`;
  };

  return (
    <Container size="xl" my="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={4}>
            {intl.formatMessage({
              id: 'uploads',
            })}
          </Title>
          <FileButton
            onChange={handleFileUpload}
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
            disabled={isUploading}
          >
            {(props) => (
              <Button
                {...props}
                leftSection={<IconUpload size={16} />}
                loading={isUploading}
              >
                {intl.formatMessage({
                  id: 'uploadFile',
                })}
              </Button>
            )}
          </FileButton>
        </Group>

        {isLoading ? (
          <Center h={200}>
            <Loader />
          </Center>
        ) : uploads.length === 0 ? (
          <Paper p="xl" withBorder>
            <Center>
              <Stack align="center" gap="md">
                <IconPhoto size={48} stroke={1.5} color="gray" />
                <Text c="dimmed" size="lg">
                  {intl.formatMessage({
                    id: 'noFilesUploaded',
                  })}
                </Text>
                <Text c="dimmed" size="sm">
                  {intl.formatMessage({
                    id: 'uploadFilesHint',
                  })}
                </Text>
              </Stack>
            </Center>
          </Paper>
        ) : (
          <Grid>
            {uploads.map((file) => (
              <Grid.Col
                key={file.filename}
                span={{ base: 12, sm: 6, md: 4, lg: 3 }}
              >
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Card.Section>
                    <Image
                      src={getImageUrl(file)}
                      alt={file.originalName}
                      height={200}
                      fit="cover"
                      fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23ddd' width='200' height='200'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='18' dy='10.5' font-weight='bold' x='50%25' y='50%25' text-anchor='middle'%3EImage%3C/text%3E%3C/svg%3E"
                    />
                  </Card.Section>

                  <Stack gap="xs" mt="md">
                    <Text
                      size="sm"
                      fw={500}
                      lineClamp={1}
                      title={file.originalName}
                    >
                      {file.originalName}
                    </Text>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <Badge size="sm" variant="light">
                          {formatFileSize(file.size)}
                        </Badge>
                        <CopyButton
                          value={getImageHtmlTag(file)}
                          timeout={2000}
                        >
                          {({ copied, copy }) => (
                            <Tooltip
                              label={
                                copied
                                  ? intl.formatMessage({
                                      id: 'copiedToClipboard',
                                    })
                                  : intl.formatMessage({
                                      id: 'copyImageTag',
                                    })
                              }
                            >
                              <ActionIcon
                                variant="light"
                                color={copied ? 'green' : 'gray'}
                                onClick={copy}
                              >
                                {copied ? (
                                  <IconCheck size={16} />
                                ) : (
                                  <IconCopy size={16} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                        <CopyButton value={getImageUrl(file)} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={
                                copied
                                  ? intl.formatMessage({
                                      id: 'copiedToClipboard',
                                    })
                                  : intl.formatMessage({
                                      id: 'copyImageUrl',
                                    })
                              }
                            >
                              <ActionIcon
                                variant="light"
                                color={copied ? 'green' : 'gray'}
                                onClick={copy}
                              >
                                {copied ? (
                                  <IconCheck size={16} />
                                ) : (
                                  <IconLink size={16} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                      <ActionIcon
                        color="red"
                        variant="light"
                        onClick={() => handleDeleteClick(file.filename)}
                        loading={isDeleting === file.filename}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>

      <Modal
        opened={deleteDialogOpened}
        onClose={handleDeleteCancel}
        title={intl.formatMessage({ id: 'delete' })}
        centered
      >
        <Text size="sm">{intl.formatMessage({ id: 'confirmDelete' })}</Text>
        {fileToDelete && (
          <Text size="sm" c="dimmed" mt="xs">
            {uploads.find((f) => f.filename === fileToDelete)?.originalName}
          </Text>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleDeleteCancel}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button color="red" onClick={handleDeleteConfirm}>
            {intl.formatMessage({ id: 'delete' })}
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};
